import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import storageService from '../../../../../utils/storageService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const RoleForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [menus, setMenus] = useState([]);

    const [formData, setFormData] = useState({
        roleName: '',
        menuListId: [],
        isActive: 'Active'
    });

    const isEdit = !!location.state?.id;
    const editItem = location.state?.item;

    const employeeCode = storageService.getItem('employeeCode') || storageService.getItem('employeeName') || '';

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Menus
                const menuRes = await apiServices({ method: 'GET', url: '/MenuList/getAllMenuList' });
                if (menuRes.success || menuRes.isSuccess || menuRes.IsSuccess || menuRes) {
                    const menuData = menuRes.data?.menuListResponse || menuRes.menuListResponse || menuRes.data || menuRes;
                    setMenus((Array.isArray(menuData) ? menuData : []).map(m => ({
                        label: m.menuName || m.MenuName,
                        value: m.menuListId || m.id || m.MenuListId || m.Id
                    })));
                }

                if (isEdit && editItem) {
                    let menuIds = editItem.menuListIds || editItem.MenuListIds || [];

                    if (Array.isArray(menuIds) && menuIds.length > 0 && typeof menuIds[0] === 'string') {
                        menuIds = menuIds[0].split(',').map(num => parseInt(num.trim(), 10)).filter(num => !isNaN(num));
                    } else if (typeof menuIds === 'string') {
                        menuIds = menuIds.split(',').map(num => parseInt(num.trim(), 10)).filter(num => !isNaN(num));
                    }

                    setFormData({
                        roleName: editItem.roleName || editItem.RoleName || '',
                        menuListId: Array.isArray(menuIds) ? menuIds : [],
                        isActive: editItem.isActive || editItem.IsActive || (editItem.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active'
                    });
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            }
        };

        fetchMasterData();
    }, [isEdit, editItem]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleMultiSelectChange = (key, options) => {
        const selectedValues = options ? options.map(opt => opt.value) : [];
        setFormData(prev => ({ ...prev, [key]: selectedValues }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const validateForm = () => {
        if (!formData.roleName || !formData.roleName.trim()) return toast.warning("Role Name is required"), false;
        if (!formData.menuListId || formData.menuListId.length === 0) return toast.warning("At least one Menu must be selected"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payload = {
            roleId: isEdit && editItem ? (editItem.roleId || editItem.RoleId || 0) : 0,
            roleName: formData.roleName,
            menuListIds: formData.menuListId.map(id => id.toString()),
            isActiveId: formData.isActive === 'Active' ? 1 : 0,
            createdBy: employeeCode || 'sys'
        };

        try {
            const url = isEdit ? `/ResidentialRoleMaster/UpdateResidentialRole/${payload.roleId}` : `/ResidentialRoleMaster/AddResidentialRole`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success || response.isSuccess || response.IsSuccess || response) {
                toast.success(`Residential Role ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentres/master-management/role');
            } else {
                toast.error(response.message || `Failed to ${isEdit ? 'update' : 'create'} role.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    const selectedMenus = menus.filter(m => formData.menuListId.includes(m.value));

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Residential Role`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} residential role.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="roleName">
                        Role Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors"
                        type="text"
                        id="roleName"
                        value={formData.roleName}
                        onChange={handleInputChange}
                        placeholder="e.g., Admin, User"
                    />
                </div>

                <div className="col-span-1 md:col-span-2 lg:col-span-1 z-50">
                    <CustomSelect
                        label={<>Select Menus <span className="text-red-500">*</span></>}
                        options={menus}
                        value={selectedMenus}
                        onChange={(opts) => handleMultiSelectChange('menuListId', opts)}
                        isMulti={true}
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentres/master-management/role")}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default RoleForm;
